import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { DropdownList, Multiselect } from 'react-widgets';
import CustomModal from '../Modal';
import FormGroup from '../FormGroup';
import { I18nContext } from 'utils/I18n/context';

const SelectGoalTree = ({
    initGoalActions,
    modalIsVisible,
    offers,
    goals,
    goalActions,
    blockedIdGoalActions,
    nesting,
    loadingOffers,
    loadingGoals,
    loadingGoalActions,
    getListOffers,
    getListGoals,
    getListGoalActions,
    onCloseModal,
    onConfirm
}) => {
    const { t } = useContext(I18nContext);
    const [state, setState] = useState({
        selectedOffer: {},
        selectedGoal: {},
        selectedGoalActions: [],
        initGoalActions: initGoalActions
    });

    const staticNames = t?.selectGoalActions || { 
        title: 'Select Goal Actions', 
        offer: 'Offer', 
        goal: 'Goal', 
        goalActions: 'Goal Actions' 
    };
    const staticActions = t?.actions || { add: 'Add' };

    const initGoalActionsHandler = useCallback(() => {
        if (nesting === 'goals') {
            getListGoals({ids: state.initGoalActions.ids});
        } else {
            getListGoalActions({ids: state.initGoalActions.ids});
        }
    }, [nesting, getListGoals, getListGoalActions, state.initGoalActions]);

    const resetSelected = useCallback(() => {
        setState(prev => ({
            ...prev,
            selectedOffer: {},
            selectedGoal: {},
            selectedGoalActions: []
        }));
    }, []);

    const disabledGoalActions = useCallback(() => {
        if (loadingGoalActions || !state.selectedGoal.value || !goalActions.length) {
            return true;
        }

        return blockedIdGoalActions;
    }, [loadingGoalActions, state.selectedGoal.value, goalActions.length, blockedIdGoalActions]);

    const disabledGoal = useCallback(() => {
        if (loadingGoals || !state.selectedOffer.value || !goals.length) {
            return true;
        }

        return blockedIdGoalActions;
    }, [loadingGoals, state.selectedOffer.value, goals.length, blockedIdGoalActions]);

    const onChangeOffer = useCallback((item) => {
        setState(prev => ({
            ...prev,
            selectedOffer: item
        }));
        getListGoals({offerIds: item.value});
    }, [getListGoals]);

    const onChangeGoal = useCallback((item) => {
        setState(prev => ({
            ...prev,
            selectedGoal: item
        }));
        getListGoalActions({goalIds: item.value});
    }, [getListGoalActions]);

    const onChangeGoalActions = useCallback((items) => {
        setState(prev => ({
            ...prev,
            selectedGoalActions: items
        }));
    }, []);

    const onCloseModalHandler = useCallback(() => {
        resetSelected();
        onCloseModal && onCloseModal();
    }, [resetSelected, onCloseModal]);

    const onConfirmHandler = useCallback(() => {
        onCloseModalHandler();
        onConfirm && onConfirm(state.selectedGoalActions);
    }, [onCloseModalHandler, onConfirm, state.selectedGoalActions]);

    useEffect(() => {
        if (!offers.length) {
            getListOffers();
        }

        if (state.initGoalActions) {
            initGoalActionsHandler();
        }
    }, [offers.length, getListOffers, state.initGoalActions, initGoalActionsHandler]);

    useEffect(() => {
        if (state.initGoalActions && (goalActions.length || goals.length)) {
            state.initGoalActions.onSuccess(nesting === 'goals' ? goals : goalActions);
            setState(prev => ({
                ...prev,
                initGoalActions: null
            }));
        }
    }, [state.initGoalActions, goalActions.length, goals.length, nesting, goals, goalActions]);

    return (
        <CustomModal
            visible={modalIsVisible}
            onClose={onCloseModalHandler}
            title={staticNames.title}
            confirmName={staticActions.add}
            confirmDisabled={!state.selectedGoalActions.length}
            onConfirm={onConfirmHandler}
        >
            <FormGroup
                label={staticNames.offer}
            >
                <DropdownList
                    className={'rw-dropdownlist-select'}
                    dataKey='id'
                    textField='title'
                    busy={loadingOffers}
                    data={offers}
                    value={offers.find(o => o.title === state.selectedOffer.title) || null}
                    onChange={onChangeOffer}
                />
            </FormGroup>
            {nesting === 'goals'
                ? (
                    <FormGroup
                        label={staticNames.goal}
                    >
                        <Multiselect
                            dataKey='value'
                            textField='title'
                            disabled={disabledGoal()}
                            busy={loadingGoals}
                            data={Array.isArray(goals) ? goals : []}
                            value={Array.isArray(state.selectedGoalActions) ? state.selectedGoalActions : []}
                            onChange={onChangeGoalActions}
                        />
                    </FormGroup>
                )
                : (
                    <React.Fragment>
                        <FormGroup
                            label={staticNames.goal}
                        >
                            <DropdownList
                                className={'rw-dropdownlist-select'}
                                dataKey='id'
                                textField='title'
                                disabled={loadingGoals || !goals.length || !state.selectedOffer.value}
                                busy={loadingGoals}
                                data={goals}
                                value={goals.find(g => g.title === state.selectedGoal.title) || null}
                                onChange={onChangeGoal}
                            />
                        </FormGroup>
                        <FormGroup
                            label={staticNames.goalActions}
                        >
                            <Multiselect
                                dataKey='id'
                                textField='title'
                                disabled={disabledGoalActions()}
                                busy={loadingGoalActions}
                                data={Array.isArray(goalActions) ? goalActions : []}
                                value={Array.isArray(state.selectedGoalActions) ? state.selectedGoalActions : []}
                                onChange={onChangeGoalActions}
                            />
                        </FormGroup>
                    </React.Fragment>
                )
            }
        </CustomModal>
    );
};

SelectGoalTree.propTypes = {
    initGoalActions: PropTypes.shape({
        ids: PropTypes.string,
        onSuccess: PropTypes.func
    }),
    modalIsVisible: PropTypes.bool,
    offers: PropTypes.array,
    goals: PropTypes.array,
    goalActions: PropTypes.array,
    blockedIdGoalActions: PropTypes.array,
    nesting: PropTypes.string,
    loadingOffers: PropTypes.bool,
    loadingGoals: PropTypes.bool,
    loadingGoalActions: PropTypes.bool,
    getListOffers: PropTypes.func,
    getListGoals: PropTypes.func,
    getListGoalActions: PropTypes.func,
    onCloseModal: PropTypes.func,
    onConfirm: PropTypes.func
};

SelectGoalTree.defaultProps = {
    initGoalActions: null,
    modalIsVisible: false,
    offers: [],
    goals: [],
    goalActions: [],
    blockedIdGoalActions: [],
    nesting: '',
    loadingOffers: false,
    loadingGoals: false,
    loadingGoalActions: false,
    getListOffers: () => {},
    getListGoals: () => {},
    getListGoalActions: () => {},
    onCloseModal: () => {},
    onConfirm: () => {}
};

export default SelectGoalTree;
